import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Edit, Users, Shield, Eye, Check, X, Calendar, User } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useRoles } from '../api/roles/use-roles';
import { Skeleton } from '@/components/ui/skeleton';
import { PageContainer, PageHeader } from '@/components/shared/layout';
import { StatusBadge } from '@/components/shared/StatusBadge';

const MODULES = [
    { key: 'USERS', label: 'Users', description: 'Manage system users and their accounts' },
    { key: 'LEADS', label: 'Leads', description: 'Manage customer leads and prospects' },
    { key: 'WALLET', label: 'Wallet', description: 'Manage user wallets and balances' },
    { key: 'BALANCES', label: 'Balances', description: 'View account balances and transactions' },
    { key: 'CUSTOMERS', label: 'Customers', description: 'Manage customer accounts and data' },
    { key: 'KYC', label: 'KYC', description: 'Handle Know Your Customer processes' },
    { key: 'ASSETS', label: 'Assets', description: 'Manage trading assets and instruments' },
    { key: 'TRADES', label: 'Trades', description: 'View and manage trade history' },
    { key: 'PRICES', label: 'Prices', description: 'Access to price data and feeds' },
    { key: 'TRANSACTIONS', label: 'Transactions', description: 'Manage financial transactions' },
    {
        key: 'SYSTEM_SETTINGS',
        label: 'System Settings',
        description: 'Configure system parameters',
    },
    { key: 'AUDIT_LOG', label: 'Audit Log', description: 'View system audit logs' },
    { key: 'API_MANAGEMENT', label: 'API Management', description: 'Manage API keys and access' },
    { key: 'ROLES', label: 'Roles', description: 'Manage user roles and permissions' },
    { key: 'TRADING_ACCOUNTS', label: 'Trading Accounts', description: 'Manage trading accounts' },
    {
        key: 'PAYMENT_METHODS',
        label: 'Payment Methods',
        description: 'Manage payment methods and configurations',
    },
    {
        key: 'NOTES',
        label: 'Notes',
        description: 'Manage notes, tasks, and activities for leads and customers',
    },
    {
        key: 'NOTIFICATIONS',
        label: 'Notifications',
        description: 'List, view, and create notifications and manual notifications',
    },
];

const SCOPES = [
    { key: 'mine', label: 'Mine', description: 'Access to own data only' },
    { key: 'my_team', label: 'My Team', description: 'Access to team data' },
    { key: 'all', label: 'All', description: 'Access to all data' },
];

const ACTIONS = [
    { key: 'view', label: 'View', description: 'Read access to data' },
    { key: 'propose', label: 'Propose', description: 'Create and submit proposals' },
    { key: 'edit', label: 'Edit', description: 'Modify and update data' },
];

const RoleDetailsPage: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { roles, isLoading, error } = useRoles();

    const role = roles?.find(r => r.id === id);

    const getPermissionSummary = (permissions: Record<string, any>) => {
        const modules = Object.keys(permissions);
        const totalPermissions = modules.length * 3 * 3;
        let grantedPermissions = 0;

        modules.forEach(module => {
            const modulePerms = permissions[module];
            ['view', 'propose', 'edit'].forEach(action => {
                ['mine', 'my_team', 'all'].forEach(scope => {
                    if (modulePerms[action]?.[scope]) {
                        grantedPermissions++;
                    }
                });
            });
        });

        return {
            total: totalPermissions,
            granted: grantedPermissions,
            percentage: Math.round((grantedPermissions / totalPermissions) * 100),
        };
    };

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });
    };

    if (isLoading) {
        return (
            <div className="space-y-6">
                <div className="flex items-center gap-4">
                    <Skeleton className="h-8 w-8" />
                    <Skeleton className="h-8 w-48" />
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    <div className="lg:col-span-1">
                        <Skeleton className="h-64 w-full" />
                    </div>
                    <div className="lg:col-span-2">
                        <Skeleton className="h-96 w-full" />
                    </div>
                </div>
            </div>
        );
    }

    if (error || !role) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <Card className="w-full max-w-md">
                    <CardContent className="pt-6">
                        <div className="text-center space-y-4">
                            <Shield className="h-12 w-12 text-destructive mx-auto" />
                            <div>
                                <h3 className="text-lg font-semibold text-destructive">
                                    Role Not Found
                                </h3>
                                <p className="text-sm text-muted-foreground mt-1">
                                    The role you're looking for doesn't exist or has been removed.
                                </p>
                            </div>
                            <Button onClick={() => navigate('/roles')}>Back to Roles</Button>
                        </div>
                    </CardContent>
                </Card>
            </div>
        );
    }

    const permissionSummary = getPermissionSummary(role.permissions);

    return (
        <PageContainer>
            <PageHeader
                title={role.name}
                description="Role details and permissions"
                breadcrumbs={[{ label: 'Roles', to: '/roles' }, { label: role.name }]}
                backTo="/roles"
                backLabel="Back to roles"
                actions={
                    <Button variant="outline" onClick={() => navigate(`/roles/${role.id}/edit`)}>
                        <Edit className="h-4 w-4" />
                        Edit Role
                    </Button>
                }
            />

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Role Information */}
                <div className="lg:col-span-1">
                    <Card>
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                                <Shield className="h-5 w-5" />
                                Role Information
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div>
                                <label className="text-sm font-medium text-muted-foreground">
                                    Name
                                </label>
                                <p className="text-lg font-semibold">{role.name}</p>
                            </div>

                            <div>
                                <label className="text-sm font-medium text-muted-foreground">
                                    Description
                                </label>
                                <p className="text-sm">
                                    {role.description || 'No description provided'}
                                </p>
                            </div>

                            <div>
                                <label className="text-sm font-medium text-muted-foreground">
                                    Type
                                </label>
                                <div className="mt-1">
                                    <StatusBadge status={role.type} kind="account" />
                                </div>
                            </div>

                            <div>
                                <label className="text-sm font-medium text-muted-foreground">
                                    System Role
                                </label>
                                <div className="mt-1">
                                    {role.is_system ? (
                                        <Badge className="bg-success/10 text-success">
                                            <Check className="h-3 w-3 me-1" />
                                            Yes
                                        </Badge>
                                    ) : (
                                        <Badge variant="outline" className="text-muted-foreground">
                                            <X className="h-3 w-3 me-1" />
                                            No
                                        </Badge>
                                    )}
                                </div>
                            </div>

                            <div className="pt-4 border-t">
                                <div className="flex items-center justify-between mb-2">
                                    <span className="text-sm font-medium">Permissions Summary</span>
                                    <Badge variant="outline">{permissionSummary.percentage}%</Badge>
                                </div>
                                <div className="w-full bg-muted rounded-full h-2">
                                    <div
                                        className="bg-primary h-2 rounded-full"
                                        style={{ width: `${permissionSummary.percentage}%` }}
                                    />
                                </div>
                                <p className="text-xs text-muted-foreground mt-1">
                                    {permissionSummary.granted} of {permissionSummary.total}{' '}
                                    permissions granted
                                </p>
                            </div>

                            <div className="pt-4 border-t space-y-2">
                                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                    <Calendar className="h-4 w-4" />
                                    <span>Created: {formatDate(role.created_at)}</span>
                                </div>
                                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                    <Calendar className="h-4 w-4" />
                                    <span>Updated: {formatDate(role.updated_at)}</span>
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    {/* Users with this role - Placeholder for future implementation */}
                    <Card className="mt-6">
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                                <User className="h-5 w-5" />
                                Assigned Users
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="text-center py-8">
                                <Users className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                                <p className="text-sm text-muted-foreground">
                                    User assignment feature coming soon
                                </p>
                            </div>
                        </CardContent>
                    </Card>
                </div>

                {/* Permissions Details */}
                <div className="lg:col-span-2">
                    <Card>
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                                <Eye className="h-5 w-5" />
                                Permissions Details
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            <Tabs defaultValue="detailed" className="w-full">
                                <TabsList className="grid w-full grid-cols-2">
                                    <TabsTrigger value="detailed">Detailed View</TabsTrigger>
                                    <TabsTrigger value="summary">Summary View</TabsTrigger>
                                </TabsList>

                                <TabsContent value="detailed" className="mt-6">
                                    <div className="space-y-6">
                                        {MODULES.map(module => {
                                            const modulePerms = role.permissions[module.key];
                                            const hasAnyPermission = ACTIONS.some(action =>
                                                SCOPES.some(
                                                    scope =>
                                                        modulePerms?.[
                                                            action.key as
                                                                | 'view'
                                                                | 'propose'
                                                                | 'edit'
                                                        ]?.[scope.key as 'mine' | 'my_team' | 'all']
                                                )
                                            );

                                            return (
                                                <div
                                                    key={module.key}
                                                    className="border rounded-lg p-4"
                                                >
                                                    <div className="flex items-start justify-between mb-4">
                                                        <div>
                                                            <h3 className="font-semibold">
                                                                {module.label}
                                                            </h3>
                                                            <p className="text-sm text-muted-foreground">
                                                                {module.description}
                                                            </p>
                                                        </div>
                                                        {hasAnyPermission ? (
                                                            <Badge className="bg-success/10 text-success">
                                                                <Check className="h-3 w-3 me-1" />
                                                                Permissions Granted
                                                            </Badge>
                                                        ) : (
                                                            <Badge
                                                                variant="outline"
                                                                className="text-muted-foreground"
                                                            >
                                                                <X className="h-3 w-3 me-1" />
                                                                No Permissions
                                                            </Badge>
                                                        )}
                                                    </div>

                                                    <div className="grid grid-cols-4 gap-4">
                                                        <div className="text-sm font-medium text-muted-foreground">
                                                            Action / Scope
                                                        </div>
                                                        {SCOPES.map(scope => (
                                                            <div
                                                                key={scope.key}
                                                                className="text-sm font-medium text-center"
                                                            >
                                                                {scope.label}
                                                            </div>
                                                        ))}

                                                        {ACTIONS.map(action => (
                                                            <React.Fragment key={action.key}>
                                                                <div className="text-sm font-medium flex items-center gap-2">
                                                                    {action.label}
                                                                    <span className="text-xs text-muted-foreground">
                                                                        {action.description}
                                                                    </span>
                                                                </div>
                                                                {SCOPES.map(scope => {
                                                                    const hasPermission =
                                                                        modulePerms?.[
                                                                            action.key as
                                                                                | 'view'
                                                                                | 'propose'
                                                                                | 'edit'
                                                                        ]?.[
                                                                            scope.key as
                                                                                | 'mine'
                                                                                | 'my_team'
                                                                                | 'all'
                                                                        ];

                                                                    return (
                                                                        <div
                                                                            key={scope.key}
                                                                            className="flex justify-center"
                                                                        >
                                                                            {hasPermission ? (
                                                                                <Check className="h-5 w-5 text-success" />
                                                                            ) : (
                                                                                <X className="h-5 w-5 text-muted-foreground/40" />
                                                                            )}
                                                                        </div>
                                                                    );
                                                                })}
                                                            </React.Fragment>
                                                        ))}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </TabsContent>

                                <TabsContent value="summary" className="mt-6">
                                    <div className="space-y-4">
                                        {MODULES.map(module => {
                                            const modulePerms = role.permissions[module.key];
                                            const hasAnyPermission = ACTIONS.some(action =>
                                                SCOPES.some(
                                                    scope =>
                                                        modulePerms?.[
                                                            action.key as
                                                                | 'view'
                                                                | 'propose'
                                                                | 'edit'
                                                        ]?.[scope.key as 'mine' | 'my_team' | 'all']
                                                )
                                            );

                                            const grantedPermissions = ACTIONS.filter(action =>
                                                SCOPES.some(
                                                    scope =>
                                                        modulePerms?.[
                                                            action.key as
                                                                | 'view'
                                                                | 'propose'
                                                                | 'edit'
                                                        ]?.[scope.key as 'mine' | 'my_team' | 'all']
                                                )
                                            ).length;

                                            return (
                                                <div
                                                    key={module.key}
                                                    className="flex items-center justify-between p-3 border rounded-lg"
                                                >
                                                    <div>
                                                        <h3 className="font-medium">
                                                            {module.label}
                                                        </h3>
                                                        <p className="text-sm text-muted-foreground">
                                                            {module.description}
                                                        </p>
                                                    </div>
                                                    <div className="flex items-center gap-2">
                                                        {hasAnyPermission ? (
                                                            <Badge className="bg-success/10 text-success">
                                                                {grantedPermissions} of{' '}
                                                                {ACTIONS.length} actions
                                                            </Badge>
                                                        ) : (
                                                            <Badge
                                                                variant="outline"
                                                                className="text-muted-foreground"
                                                            >
                                                                <X className="h-3 w-3 me-1" />
                                                                No Permissions
                                                            </Badge>
                                                        )}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </TabsContent>
                            </Tabs>
                        </CardContent>
                    </Card>
                </div>
            </div>
        </PageContainer>
    );
};

export default RoleDetailsPage;
